// src/app/components/user-bank-account/user-bank-account.component.ts

import {
  Component,
  OnChanges,
  SimpleChanges,
  OnInit,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';

import {
  WalletService,
  WalletOperation,
} from '../../services/wallet/wallet.service';
import { Wallet } from 'src/app/model/Wallet';
import { UserService } from 'src/app/services/user/user.service';

@Component({
  selector: 'app-user-bank-account',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './user-bank-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./user-bank-account.component.scss'],
})
export class UserBankAccountComponent implements OnInit {
  /** Dependency injections */
  private _walletService: WalletService = inject(WalletService);
  private _userService: UserService = inject(UserService);
  private _toastrService: ToastrService = inject(ToastrService);

  // Wallet collection
  public wallets: Wallet[] = [];

  // Input property to receive wallet data
  public activeWallet: any = {
    bankNickname: '',
    bankName: '',
    accountNumber: '',
    accountType: null as number | null,
    routingNumber: '',
    status: '',
    exists: false,
    editing: false,
  };
  public defaultWallet: any = Object.assign({}, this.activeWallet);

  // Error messages for form validation
  public errorMessage = '';
  public errorMsgs = {
    requiredField: 'This field is required',
    invalidAccountNumber: 'Please enter a valid account number',
    invalidRoutingNumber: 'Please enter a valid 9-digit routing number',
    asteriskNotAllowed: 'Account number cannot contain asterisks (*)',
  };

  // State variables
  public bankAccountExists = false;
  public editingBankAccount = false;
  public isSubmitting = false;
  public isSubmittingDefaultId: string = '';
  public isDeletingWalletId?: string;
  public isLoading = true;

  // Form input options
  private achWalletId: string | null = null;

  /**
   * On initialization,
   */
  ngOnInit(): void {
    this.isLoading = true;

    // Subscribe to wallets observable to keep wallets updated
    this._walletService.wallets$.subscribe((wallets) => {
      if (wallets == undefined) {
        return;
      }

      this.wallets = wallets;
      this.isLoading = false;
    });

    // Fetch initial wallets
    const wallets = this._walletService.getWallets();
    if (wallets != undefined) {
      this.wallets = wallets;
      this.isLoading = false;
    }
  }

  /**
   * Remove a bank account by its ACH wallet ID
   *
   * @param id The ACH wallet ID of the account to remove
   */
  removeAccount(id: string): void {
    this.isDeletingWalletId = id;

    // Call the wallet service to delete the wallet
    try {
      this._walletService
        .deleteWallet$(id)
        .pipe(finalize(() => (this.isDeletingWalletId = undefined)))
        .subscribe({
          next: () => {
            // On success
            this._toastrService.success('Wallet removed successfully');
          },
          error: (err) => {
            // Catch HTTP errors
            console.error('[USER-BANK-ACCOUNT]: failed to remove wallet', err);
            this._toastrService.error('Failed to remove wallet');
          },
        });
    } catch (err: any) {
      // Catch synchronous errors
      console.error('[USER-BANK-ACCOUNT]: failed to remove wallet', err);
      this._toastrService.error(err.message || 'Failed to remove wallet');
      this.isDeletingWalletId = undefined;
    }
  }

  /**
   * Set a wallet as the default wallet
   *
   * @param achWalletId The ACH wallet ID to set as default
   */
  setAsDefault(achWalletId: string): void {
    this.isSubmittingDefaultId = achWalletId;

    try {
      this._walletService
        .setDefaultWallet$(achWalletId)
        .pipe(finalize(() => (this.isSubmittingDefaultId = '')))
        .subscribe({
          next: () => {
            // On success
            this._toastrService.success('Default wallet updated');
          },
          error: (err) => {
            // Catch HTTP errors
            console.error(
              '[USER-BANK-ACCOUNT]: failed to set default wallet',
              err,
            );
            this._toastrService.error('Failed to set default wallet');
          },
        });
    } catch (err: any) {
      // Catch synchronous errors
      console.error('[USER-BANK-ACCOUNT]: failed to set default wallet', err);
      this._toastrService.error(err.message || 'Failed to set default wallet');
      this.isSubmittingDefaultId = '';
    }
  }

  /**
   * Edit an existing bank account
   *
   * @param wallet The wallet to edit
   */
  editExisting(wallet: Wallet): void {
    // Populate the form with existing wallet data
    this.activeWallet.bankNickname = wallet.nickname;
    this.activeWallet.bankName = wallet.bankName;
    this.activeWallet.accountNumber = wallet.accountNumber;
    this.activeWallet.routingNumber = wallet.abaNumber;
    this.activeWallet.accountType = +wallet.accountType;
    this.activeWallet.status = wallet.active === '1' ? 'Active' : 'Inactive';

    // Store ach wallet id in form data
    this.achWalletId = wallet.achwalletID ?? wallet.id ?? null;

    // Set state flags
    this.bankAccountExists = true;
    this.editingBankAccount = true;
  }

  /**
   * Add a new bank account/wallet
   */
  addWallet(): void {
    this.achWalletId = null;
    this.bankAccountExists = false;
    this.editingBankAccount = true;

    this.activeWallet = Object.assign({}, this.defaultWallet);
  }

  /**
   * Save the bank account (add or update). Add or update is determined by the
   * flag `bankAccountExists`.
   */
  saveBankAccount(): void {
    this.isSubmitting = true;

    const isAdd = !this.bankAccountExists;

    // append [p] only on ADD
    const nickname = isAdd
      ? `${this.activeWallet.bankNickname} [p]`
      : this.activeWallet.bankNickname;

    // Construct wallet operation data
    const walletData: WalletOperation = {
      operation: isAdd ? 'ADD' : 'UPDATE',
      accountType: this.activeWallet.accountType!,
      nickname: nickname,
      accountNumber: this.activeWallet.accountNumber,
      abaNumber: this.activeWallet.routingNumber,
      validated: '1',
      active: '0',
    };

    try {
      // Perform action with wallet service
      const walletOp$ =
        isAdd && !this.achWalletId
          ? this._walletService.addWallet$(walletData)
          : this._walletService.updateWallet$(this.achWalletId!, walletData);

      // Subscribe to the observable to handle response
      walletOp$
        .pipe(
          finalize(() => setTimeout(() => (this.isSubmitting = false), 1000)),
        )
        .subscribe({
          next: () => {
            // On success
            this.editingBankAccount = false;
            this._toastrService.success(
              'Bank account saved successfully!',
              'Success',
            );
          },
          error: (err) => {
            // Catch HTTP errors
            console.error('[ERR]: Failed to save bank account', err);
            this._toastrService.error('Failed to save bank account');
          },
        });
    } catch (err: any) {
      // Catch synchronous errors
      console.error('[ERR]: Failed to save bank account', err);
      this._toastrService.error(err.message || 'Failed to save bank account');
      this.isSubmitting = false;
    }
  }

  stripPendingTag(n: string | undefined | null): string {
    return (n || '').replace(/\s*\[p\]\s*$/i, '');
  }

  /**
   * Cancel editing the bank account and revert any changes
   */
  cancelEditBankAccount(): void {
    this.editingBankAccount = false;
  }

  /**
   * Stop the asterisk character from being entered in the account number field
   *
   * @param event The keyboard event
   * @returns false if the character is prevented, true otherwise
   */
  preventInvalidChars(event: KeyboardEvent): boolean {
    if (event.key === '*') {
      event.preventDefault();
      return false;
    }
    return true;
  }
}
